import React, { useState, useEffect } from 'react'
import { BILLING_DECIMAL_PLACES } from '../../constants/billingConstants';
import withBase from '../../hoc/withBase';
import { connect, useDispatch, useSelector } from 'react-redux';
import * as billingActions from '../../store/actions/billing';
import { setBillingEnabled } from '../../store/actions/soracomUsers';

// Tab Components
import {
  MasterSheetTab,
  OperatorOverridesTab,
  RequiresReviewTab,
  AuditLogTab,
} from './MasterSheet/';

// Layout
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';

// Material-UI core
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Drawer from '@material-ui/core/Drawer';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Badge from '@material-ui/core/Badge';
import Fab from '@material-ui/core/Fab';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';


// Icons
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import WarningIcon from '@material-ui/icons/Warning';
import HistoryIcon from '@material-ui/icons/History';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import RateReviewIcon from '@material-ui/icons/RateReview';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  search: { flex: 1, minWidth: 220 },
  flagged: { backgroundColor: '#fff8e1' },
  missingMaster: { backgroundColor: '#fce4ec' },
  excluded: { backgroundColor: '#f5f5f5', opacity: 0.75 },
  drawer: { width: 580 },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  drawerBody: { padding: theme.spacing(2), overflowY: 'auto' },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: { margin: theme.spacing(2, 0, 1) },
  chip: { marginRight: 4 },
  pendingBadge: { marginLeft: theme.spacing(1) },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1300,
  },
  masterHint: {
    background: '#f3f8ff',
    border: '1px solid #bbdefb',
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  readonlyField: { opacity: 0.7 },
  operatorPanel: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: theme.spacing(2),
  },
  operatorList: {
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    height: 480,
    overflowY: 'scroll',
  },
  operatorListItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': { background: '#f5f5f5' },
  },
  operatorListItemSelected: {
    background: '#e3f2fd',
    borderLeft: '3px solid #1976d2',
  },
}));

// ─── Master-only fields (shared reference — operator cannot override) ─────────
const MASTER_ONLY_FIELDS = ['billingItemName', 'displayItemName', 'chargeCode', 'unit', 'soracomInvoiceItemName', 'GSTApplicable', 'GSTPercentage'];
const OPERATOR_OVERRIDABLE_FIELDS = ['purchasePrice', 'salePrice'];
const ALL_CSV_FIELDS = [...MASTER_ONLY_FIELDS, ...OPERATOR_OVERRIDABLE_FIELDS];

// ─── CSV column maps ──────────────────────────────────────────────────────────
// Keys   = MongoDB field names (used internally)
// Values = Friendly CSV header names (shown in the exported file)
// The import parser uses this map in reverse to translate uploaded headers back
// to field names before diffing / applying changes.

const MASTER_CSV_COLUMNS = {
  billingItemName: 'Billing Item Name',
  displayItemName: 'Display Name',
  chargeCode: 'Charge Code',
  unit: 'Unit',
  soracomInvoiceItemName: 'Soracom Invoice Item Name',
  purchasePrice: 'Master Purchase Price (USD)',
  salePrice: 'Master Sale Price (USD)',
  GSTApplicable: 'GST Applicable',
  GSTPercentage: 'GST Percentage',
};

const OPERATOR_CSV_COLUMNS = {
  billingItemName: 'Billing Item Name',
  purchasePrice: 'Operator Purchase Price (USD)',
  salePrice: 'Operator Sale Price (USD)',
  useMaster: 'Use Master Pricing',
  excludeFromBilling: 'Exclude From Billing',
  GSTApplicable: 'GST Applicable',
  GSTPercentage: 'GST Percentage',
};

const REVIEW_CSV_COLUMNS = {
  operatorId: 'Operator ID',
  rawBillingItemName: 'Raw Billing Item Name',
  billingItemName: 'Billing Item Name',
  displayItemName: 'Display Name',
  chargeCode: 'Charge Code',
  unit: 'Unit',
  soracomInvoiceItemName: 'Soracom Invoice Item Name',
  masterPurchasePrice: 'Master Purchase Price (USD)',
  masterSalePrice: 'Master Sale Price (USD)',
  GSTApplicable: 'GST Applicable',
  GSTPercentage: 'GST Percentage',
  useMaster: 'Use Master Pricing',
  operatorPurchasePrice: 'Operator Purchase Price (USD)',
  operatorSalePrice: 'Operator Sale Price (USD)',
  excludeFromBilling: 'Exclude From Billing',
};

// Helpers that work with column maps instead of raw field arrays
const rowsToCsvMapped = (columnMap, rows) => {
  const fields = Object.keys(columnMap);
  const headers = Object.values(columnMap);
  const body = rows.map(r =>
    fields.map(f => {
      const val = r[f] ?? '';
      return JSON.stringify(val);
    }).join(',')
  ).join('\n');
  return `${headers.join(',')}\n${body}`;
};

const downloadCsvMapped = (columnMap, rows, filename) => {
  const blob = new Blob([rowsToCsvMapped(columnMap, rows)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Parses a CSV that was exported with friendly headers and converts back to field names.
// Unknown headers are kept as-is so the diff still shows them.
const parseCsvMapped = (text, columnMap) => {
  const friendlyToField = Object.fromEntries(
    Object.entries(columnMap).map(([field, header]) => [header, field])
  );
  const [headerLine, ...lines] = text.trim().split('\n');
  const headers = headerLine.split(',').map(h => h.replace(/^"|"$/g, '').trim());
  return lines.map(line => {
    const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|^(?=,)|(?<=,)$)/g) || [];
    const obj = {};
    headers.forEach((h, i) => {
      const field = friendlyToField[h] || h;  // fall back to raw header if not in map
      let v = (vals[i] || '').replace(/^"|"$/g, '');
      // Handle boolean values
      if (v === 'true' || v === 'TRUE') v = true;
      else if (v === 'false' || v === 'FALSE') v = false;
      // Handle numeric values
      else if (!isNaN(v) && v !== '') v = Number(v);
      obj[field] = v;
    });
    return obj;
  });
};

const diffRows = (keyField, original, uploaded) =>
  uploaded.map(up => {
    const orig = original.find(o => o[keyField] === up[keyField]);
    if (!orig) return { row: up, status: 'NEW', changes: [] };
    const changes = ALL_CSV_FIELDS.filter(f => String(orig[f] ?? '') !== String(up[f] ?? ''))
      .map(f => ({ field: f, from: orig[f], to: up[f] }));
    return { row: up, status: changes.length ? 'MODIFIED' : 'UNCHANGED', changes };
  });

// ─── Empty form state ─────────────────────────────────────────────────────────

const emptyItem = { billingItemName: '', displayItemName: '', chargeCode: '', unit: '', soracomInvoiceItemName: '', GSTApplicable: true, GSTPercentage: 10, purchasePrice: '', salePrice: '', excludeFromBilling: false };

const ACTION_COLORS = {
  BILLING_MASTER_ITEM_CREATED: 'primary',
  BILLING_MASTER_ITEM_UPDATED: 'default',
  BILLING_MASTER_ITEM_DEACTIVATED: 'default',
  BILLING_OPERATOR_ITEM_CREATED: 'primary',
  BILLING_OPERATOR_ITEM_UPDATED: 'default',
  BILLING_REVIEW_ITEM_RESOLVED: 'primary',
  BILLING_REVIEW_ITEM_DISMISSED: 'default',
};

// ─── Component ────────────────────────────────────────────────────────────────

const MasterSheet = (props) => {
  const dispatch = useDispatch();
  const {
    masterItems,    // { data: [], total, page, limit }
    operators,      // []
    operatorItems,  // []  — replaced when operator selected
    reviewItems,    // { data: [], total, page, limit }
    activityLogs,   // { data: [], nextCursor: null }
  } = useSelector(state => state.billing);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => { dispatch(billingActions.fetchOperators()); }, []);

  const classes = useStyles();

  // Tabs: 0=Master, 1=Operator, 2=Requires Review, 3=Audit
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [masterPage, setMasterPage] = useState(0);
  const [masterRowsPerPage, setMasterRowsPerPage] = useState(50);
  const [actionFilter, setActionFilter] = useState('ALL');

  // Operator tab state
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [operatorSearch, setOperatorSearch] = useState('');

  // Review tab pagination
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewPerPage, setReviewPerPage] = useState(100);
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewOperatorFilter, setReviewOperatorFilter] = useState('ALL');

  // Detail drawer
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});

  // Review item drawer
  const [reviewSelected, setReviewSelected] = useState(null);
  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [reviewEditValues, setReviewEditValues] = useState({});

  // History drawer
  const [historyItem, setHistoryItem] = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  // CSV diff dialog
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [diffResults, setDiffResults] = useState([]);
  const [diffContext, setDiffContext] = useState('');
  const [applyingChanges, setApplyingChanges] = useState(false);

  // CSV import results dialog (for review items)
  const [importResultsDialogOpen, setImportResultsDialogOpen] = useState(false);

  const showRequestError = (err, fallback = 'Request failed.') => {
    props.showNotification({
      color: 'danger',
      message: err?.response?.data?.message || err?.message || fallback,
      time: 10000,
    });
  };
  const [importResults, setImportResults] = useState(null);

  // Add item dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addTarget, setAddTarget] = useState('master'); // 'master' | 'operator'
  const [addForm, setAddForm] = useState({ ...emptyItem });
  const [addOperatorId, setAddOperatorId] = useState('');
  const [addMasterRef, setAddMasterRef] = useState(null);
  const [addOperatorSearch, setAddOperatorSearch] = useState('');
  const [addMasterSearch, setAddMasterSearch] = useState('');

  useEffect(() => {
    dispatch(billingActions.fetchActivityLogs());
  }, []);

  // Fetch master items when page, rowsPerPage, or search changes (server-side)
  useEffect(() => {
    dispatch(billingActions.fetchMasterItems({
      page: masterPage + 1,
      limit: masterRowsPerPage,
      search: search || undefined,
    }));
  }, [masterPage, masterRowsPerPage, search]);

  // Reset master page when search changes
  useEffect(() => { setMasterPage(0); }, [search]);

  // Fetch review items when tab, page, or filters change
  useEffect(() => {
    if (tab === 2) {
      // Fetch review items with server-side pagination
      dispatch(billingActions.fetchReviewItems({
        page: reviewPage + 1, // Backend uses 1-indexed pages
        limit: reviewPerPage,
        operatorId: reviewOperatorFilter !== 'ALL' ? reviewOperatorFilter : undefined,
        rawBillingItemName: reviewSearch || undefined,
      }));
    }
  }, [tab, reviewPage, reviewPerPage, reviewSearch, reviewOperatorFilter]);

  // Fetch operator items when operator is selected
  useEffect(() => {
    if (selectedOperator) dispatch(billingActions.fetchOperatorItems(selectedOperator.operatorId));
  }, [selectedOperator]);

  // Simulate async fetch when operator is selected
  const selectOperator = (op) => {
    setSelectedOperator(op);
    setOperatorLoading(true);
    setOperatorSearch('');
  };

  const handleBillingToggle = (operatorId, newValue) => {
    dispatch(setBillingEnabled(operatorId, newValue, (err) => {
      if (err) {
        props.showNotification({
          color: 'danger',
          message: err?.response?.data?.message || 'Failed to update billing setting.',
          time: 5000,
        });
        return;
      }
      // Update selected operator state so toggle reflects immediately
      setSelectedOperator((prev) => prev ? { ...prev, billingEnabled: newValue } : prev);
      // Refresh operators list so sidebar billing status updates
      dispatch(billingActions.fetchOperators());
      props.showNotification({
        color: 'success',
        message: `Billing ${newValue ? 'enabled' : 'disabled'} for operator ${operatorId}.`,
        time: 3000,
      });
    }));
  };

  // Clear loading state once operator items land in Redux
  useEffect(() => {
    if (selectedOperator) setOperatorLoading(false);
  }, [operatorItems]);

  const handleFieldChange = (key, value) => {
    if (key === 'useMaster' && value === true) {
      // When useMaster is enabled, set prices to 0 (inherit from master)
      setEditValues(prev => ({ ...prev, [key]: value, purchasePrice: 0, salePrice: 0 }));
    } else {
      setEditValues(prev => ({ ...prev, [key]: value }));
    }
  };
  const openDetail = (row) => {
    setSelected(row);
    // Ensure boolean fields have proper values (default to true for useMaster if undefined)
    setEditValues({
      ...row,
      useMaster: row.useMaster !== undefined ? row.useMaster : true,
      excludeFromBilling: !!row.excludeFromBilling,
      GSTApplicable: row.GSTApplicable !== undefined ? row.GSTApplicable : true
    });
    setDrawerOpen(true);
    setEditMode(false);
  };
  const closeDetail = () => { setDrawerOpen(false); setSelected(null); setEditValues({}); };

  const openReviewDetail = (item) => {
    // item is now enriched by the backend with masterExists + master field data
    const masterExists = item.masterExists;
    const reviewType = masterExists ? 'MISSING_FOR_OPERATOR' : 'MISSING_FROM_MASTER';
    const operatorName = (operators || []).find(o => o.operatorId === item.operatorId)?.name || '';
    setReviewSelected({ ...item, reviewType, operatorName, billingItemName: item.rawBillingItemName });
    setReviewEditValues({
      billingItemName: item.rawBillingItemName || '',
      displayItemName: item.masterDisplayItemName || '',
      chargeCode: item.masterChargeCode || '',
      unit: item.masterUnit || '',
      soracomInvoiceItemName: item.masterSoracomInvoiceItemName || item.rawBillingItemName || '',
      masterPurchasePrice: item.masterPurchasePrice ?? '',
      masterSalePrice: item.masterSalePrice ?? '',
      GSTApplicable: item.masterGSTApplicable !== undefined ? item.masterGSTApplicable : true,
      GSTPercentage: item.masterGSTPercentage ?? 10,
      useMaster: true,
      operatorPurchasePrice: '',
      operatorSalePrice: '',
      excludeFromBilling: false,
    });
    setReviewDrawerOpen(true);
  };

  // ── Data derived from Redux state ───────────────────────────────────────────

  const allMasterItems = (masterItems?.data || (Array.isArray(masterItems) ? masterItems : [])).map(i => ({ ...i, isMaster: true }));

  // Flatten populated masterItemId ref into a flat object the table can render directly
  const allFlatOperatorItems = (operatorItems || []).map(i => {
    const m = i.masterItemId || {};
    return {
      _id: i._id, isMaster: false, operatorId: i.operatorId,
      billingItemName: m.billingItemName || '', displayItemName: m.displayItemName || '',
      chargeCode: m.chargeCode || '', unit: m.unit || '',
      soracomInvoiceItemName: m.soracomInvoiceItemName || '',
      useMaster: i.useMaster !== undefined ? i.useMaster : true,
      GSTApplicable: i.GSTApplicable !== undefined ? i.GSTApplicable : m.GSTApplicable,
      GSTPercentage: i.GSTPercentage !== undefined ? i.GSTPercentage : m.GSTPercentage,
      purchasePrice: i.purchasePrice, salePrice: i.salePrice,
      excludeFromBilling: i.excludeFromBilling, masterItemId: i.masterItemId,
    };
  });

  const masterRef = selected && !selected.isMaster
    ? allMasterItems.find(m => m.billingItemName === selected.billingItemName)
    : null;
  const operatorOverrides = selected?.isMaster
    ? allFlatOperatorItems.filter(o => o.billingItemName === selected.billingItemName)
    : [];
  const groupedItems = selected && allMasterItems.filter(m => m.displayItemName === selected.displayItemName).length > 1
    ? { displayItemName: selected.displayItemName, items: allMasterItems.filter(m => m.displayItemName === selected.displayItemName).map(m => m.billingItemName) }
    : null;

  // Server handles filtering — allMasterItems is already the correct page/search result
  const filteredMaster = allMasterItems;

  const filteredOperatorItems = allFlatOperatorItems.filter(r =>
    r.billingItemName.toLowerCase().includes(search.toLowerCase()) ||
    r.displayItemName.toLowerCase().includes(search.toLowerCase())
  );

  // Server-side pagination for review items
  const reviewItemsData = reviewItems?.data || [];
  const totalReviewCount = reviewItems?.total || 0;
  const reviewOperatorIds = reviewItems?.operatorIds || [];

  // Filter operators to only show those with pending reviews
  const operatorsWithReviews = operators.filter(op => reviewOperatorIds.includes(op.operatorId));

  const allActivityLogs = activityLogs?.data || [];
  const auditFiltered = allActivityLogs.filter(a => actionFilter === 'ALL' || a.action === actionFilter);

  // ── CSV export — fetches fresh from server then triggers browser download ──

  const handleExport = (context) => {
    if (context === 'master') {
      dispatch(billingActions.exportMasterItems((err, data) => {
        if (!err) downloadCsvMapped(MASTER_CSV_COLUMNS, data, 'master-sheet.csv');
      }));
    } else if (context === 'operator' && selectedOperator) {
      dispatch(billingActions.exportOperatorItems(selectedOperator.operatorId, (err, data) => {
        if (!err) downloadCsvMapped(OPERATOR_CSV_COLUMNS, data.map(i => {
          const m = i.masterItemId || {};
          return {
            billingItemName: m.billingItemName,
            purchasePrice: i.purchasePrice,
            salePrice: i.salePrice,
            useMaster: i.useMaster,
            excludeFromBilling: i.excludeFromBilling,
            GSTApplicable: i.GSTApplicable,
            GSTPercentage: i.GSTPercentage,
          };
        }), `operator-${selectedOperator.operatorId}.csv`);
      }));
    } else if (context === 'review') {
      dispatch(billingActions.exportReviewItems({}, (err, data) => {
        if (!err) downloadCsvMapped(REVIEW_CSV_COLUMNS, data, 'requires-review.csv');
      }));
    }
  };

  // ── CSV import — backend diffs, frontend shows dialog, then applies ─────────
  const [pendingImportRows, setPendingImportRows] = useState([]);
  const [pendingImportContext, setPendingImportContext] = useState('');

  const _flattenDiff = (result) => [
    ...(result.new || []).map(({ row }) => ({ ...row, _diffStatus: 'NEW' })),
    ...(result.modified || []).map(({ row, changes }) => ({ ...row, _diffStatus: 'MODIFIED', _changes: changes })),
    ...(result.unchanged || []).filter(r => !r.skipped).map(({ row }) => ({ ...row, _diffStatus: 'UNCHANGED' })),
  ];

  const handleCsvUpload = (e, context) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const map = context === 'master' ? MASTER_CSV_COLUMNS
        : context === 'operator' ? OPERATOR_CSV_COLUMNS : REVIEW_CSV_COLUMNS;
      const parsed = parseCsvMapped(ev.target.result, map);

      // Review items use direct import (no preview/diff)
      if (context === 'review') {
        setPendingImportRows(parsed);
        setPendingImportContext(context);
        dispatch(billingActions.importReviewItems(parsed, (err, result) => {
          if (!err) {
            // Show results in a dialog
            setImportResults(result);
            setImportResultsDialogOpen(true);
            // Refresh review items
            if (tab === 2) {
              dispatch(billingActions.fetchReviewItems({
                page: reviewPage + 1,
                limit: reviewPerPage,
                operatorId: reviewOperatorFilter !== 'ALL' ? reviewOperatorFilter : undefined,
                rawBillingItemName: reviewSearch || undefined,
              }));
            }
          }
        }));
      } else {
        // Master and operator items use preview/diff flow
        setPendingImportRows(parsed);
        setPendingImportContext(context);
        const cb = (err, result) => {
          if (err) {
            showRequestError(err, 'Import preview failed.');
            return;
          }

          setDiffResults(_flattenDiff(result));
          setDiffContext(context);
          setDiffDialogOpen(true);
        };
        dispatch(context === 'master'
          ? billingActions.previewMasterImport(parsed, cb)
          : billingActions.previewOperatorImport(selectedOperator?.operatorId, parsed, cb)
        );
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleApplyChanges = () => {
    setApplyingChanges(true);
    const cb = (err) => {
      setApplyingChanges(false);
      if (err) {
        showRequestError(err, 'Import failed.');
        return;
      }

      if (pendingImportContext === 'master') dispatch(billingActions.fetchMasterItems());
      else dispatch(billingActions.fetchOperatorItems(selectedOperator.operatorId));
      setDiffDialogOpen(false);
    };
    dispatch(pendingImportContext === 'master'
      ? billingActions.applyMasterImport(pendingImportRows, 'import.csv', cb)
      : billingActions.applyOperatorImport(selectedOperator?.operatorId, pendingImportRows, 'import.csv', cb)
    );
  };

  const handleSave = () => {
    const action = selected.isMaster
      ? billingActions.updateMasterItem(selected._id, editValues, (err) => {
        if (!err) { dispatch(billingActions.fetchMasterItems()); setDrawerOpen(false); setEditMode(false); }
      })
      : billingActions.updateOperatorItem(selected._id, {
        useMaster: editValues.useMaster,
        GSTApplicable: editValues.GSTApplicable,
        GSTPercentage: editValues.GSTPercentage,
        purchasePrice: editValues.purchasePrice,
        salePrice: editValues.salePrice,
        excludeFromBilling: editValues.excludeFromBilling
      }, (err) => {
        if (!err && selectedOperator) { dispatch(billingActions.fetchOperatorItems(selectedOperator.operatorId)); setDrawerOpen(false); setEditMode(false); }
      });
    dispatch(action);
  };

  const handleApprove = () => {
    dispatch(billingActions.approveMasterItem(selected._id, (err) => {
      if (!err) { dispatch(billingActions.fetchMasterItems()); setDrawerOpen(false); }
    }));
  };

  const handleDeactivate = () => {
    dispatch(billingActions.deactivateMasterItem(selected._id, (err) => {
      if (!err) { dispatch(billingActions.fetchMasterItems()); setDrawerOpen(false); }
    }));
  };

  const handleAddSave = () => {
    if (addTarget === 'master') {
      dispatch(billingActions.createMasterItem(addForm, (err) => {
        if (!err) { dispatch(billingActions.fetchMasterItems()); setAddDialogOpen(false); setAddForm(emptyItem); }
      }));
    } else if (addTarget === 'operator' && selectedOperator && addMasterRef) {
      dispatch(billingActions.createOperatorItem(selectedOperator.operatorId, {
        masterItemId: addMasterRef._id,
        useMaster: addForm.useMaster !== false, // Default to true
        GSTApplicable: addForm.GSTApplicable !== undefined ? addForm.GSTApplicable : true,
        GSTPercentage: addForm.GSTPercentage || 10,
        purchasePrice: addForm.purchasePrice || 0,
        salePrice: addForm.salePrice || 0,
        excludeFromBilling: addForm.excludeFromBilling || false,
      }, (err) => {
        if (!err) { dispatch(billingActions.fetchOperatorItems(selectedOperator.operatorId)); setAddDialogOpen(false); setAddForm(emptyItem); }
      }));
    }
  };

  const handleResolve = (reviewItem, resolvedMasterData, resolvedOperatorData) => {
    dispatch(billingActions.resolveReviewItem(reviewItem._id, resolvedMasterData, resolvedOperatorData, (err) => {
      if (!err) {
        dispatch(billingActions.fetchReviewItems({
          page: reviewPage + 1,
          limit: reviewPerPage,
          operatorId: reviewOperatorFilter !== 'ALL' ? reviewOperatorFilter : undefined,
          rawBillingItemName: reviewSearch || undefined,
        }));
        setReviewDrawerOpen(false);
      }
    }));
  };

  const handleDismiss = (reviewItem, dismissNote) => {
    dispatch(billingActions.dismissReviewItem(reviewItem._id, dismissNote, (err) => {
      if (!err) {
        dispatch(billingActions.fetchReviewItems({
          page: reviewPage + 1,
          limit: reviewPerPage,
          operatorId: reviewOperatorFilter !== 'ALL' ? reviewOperatorFilter : undefined,
          rawBillingItemName: reviewSearch || undefined,
        }));
        setReviewDrawerOpen(false);
      }
    }));
  };

  const handleAddMasterRefChange = (masterItem) => {
    setAddMasterRef(masterItem);
    if (masterItem) setAddForm(p => ({ ...p, purchasePrice: masterItem.purchasePrice, salePrice: masterItem.salePrice }));
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Master Sheet</Typography>
              </Box>
            </CardHeader>
            <CardBody>
              <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearch(''); }} indicatorColor="primary" textColor="primary">
                <Tab label="Master Sheet" />
                <Tab label="Operator Overrides" />
                <Tab label={
                  <Badge badgeContent={totalReviewCount} color="error" overlap="rectangular">
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <RateReviewIcon fontSize="small" /> Requires Review
                    </Box>
                  </Badge>
                } />
                <Tab label="Audit Log" icon={<HistoryIcon fontSize="small" />} />
              </Tabs>
              <Divider style={{ marginBottom: 16 }} />

              {/* ══════════════════ TAB 0 — MASTER SHEET ══════════════════ */}
              {tab === 0 && (
                <MasterSheetTab
                  classes={classes}
                  search={search}
                  onSearchChange={setSearch}
                  filteredMaster={filteredMaster}
                  allFlatOperatorItems={allFlatOperatorItems}
                  onOpenDetail={openDetail}
                  onOpenHistory={(row) => { setHistoryItem(row); setHistoryDrawerOpen(true); }}
                  onExport={handleExport}
                  onCsvUpload={handleCsvUpload}
                  page={masterPage}
                  rowsPerPage={masterRowsPerPage}
                  totalCount={masterItems?.total || 0}
                  onPageChange={(_, newPage) => setMasterPage(newPage)}
                  onRowsPerPageChange={(e) => { setMasterRowsPerPage(parseInt(e.target.value, 10)); setMasterPage(0); }}
                />
              )}

              {/* ══════════════════ TAB 1 — OPERATOR OVERRIDES ══════════════════ */}
              {tab === 1 && (
                <OperatorOverridesTab
                  classes={classes}
                  operators={operators}
                  selectedOperator={selectedOperator}
                  operatorSearch={operatorSearch}
                  search={search}
                  operatorLoading={operatorLoading}
                  filteredOperatorItems={filteredOperatorItems}
                  allMasterItems={allMasterItems}
                  onSelectOperator={selectOperator}
                  onOperatorSearchChange={setOperatorSearch}
                  onSearchChange={setSearch}
                  onOpenDetail={openDetail}
                  onOpenHistory={(row) => { setHistoryItem(row); setHistoryDrawerOpen(true); }}
                  onExport={handleExport}
                  onCsvUpload={handleCsvUpload}
                  onBillingToggle={handleBillingToggle}
                />
              )}

              {/* ══════════════════ TAB 2 — REQUIRES REVIEW ══════════════════ */}
              {tab === 2 && (
                <RequiresReviewTab
                  classes={classes}
                  reviewSearch={reviewSearch}
                  reviewOperatorFilter={reviewOperatorFilter}
                  operators={operatorsWithReviews}
                  reviewItems={reviewItemsData}
                  totalReviewCount={totalReviewCount}
                  reviewPage={reviewPage}
                  reviewPerPage={reviewPerPage}
                  onReviewSearchChange={setReviewSearch}
                  onReviewOperatorFilterChange={setReviewOperatorFilter}
                  onReviewPageChange={setReviewPage}
                  onReviewPerPageChange={setReviewPerPage}
                  onOpenReviewDetail={openReviewDetail}
                  onExport={handleExport}
                  onCsvUpload={handleCsvUpload}
                />
              )}

              {/* ══════════════════ TAB 3 — AUDIT LOG ══════════════════ */}
              {tab === 3 && (
                <AuditLogTab
                  classes={classes}
                  actionFilter={actionFilter}
                  auditFiltered={auditFiltered}
                  onActionFilterChange={setActionFilter}
                />
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ══════════════════ FAB ══════════════════ */}
      <Tooltip title="Add new line item">
        <Fab color="primary" className={classes.fab}
          onClick={() => { setAddTarget(tab === 1 ? 'operator' : 'master'); setAddForm({ ...emptyItem }); setAddMasterRef(null); setAddDialogOpen(true); }}>
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* ══════════════════ ADD ITEM DIALOG ══════════════════ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Line Item</DialogTitle>
        <DialogContent dividers>
          <Box mb={2} display="flex">
            <Button
              size="small" color="primary"
              variant={addTarget === 'master' ? 'contained' : 'outlined'}
              style={{ borderRadius: '4px 0 0 4px', borderRight: 'none' }}
              onClick={() => { setAddTarget('master'); setAddForm({ ...emptyItem }); setAddMasterRef(null); }}
            >Master Sheet</Button>
            <Button
              size="small" color="primary"
              variant={addTarget === 'operator' ? 'contained' : 'outlined'}
              style={{ borderRadius: '0 4px 4px 0' }}
              onClick={() => setAddTarget('operator')}
            >Operator Override</Button>
          </Box>

          {addTarget === 'operator' && (
            <Box mb={2}>
              {/* ── Searchable Operator picker ── */}
              <FormControl size="small" variant="outlined" fullWidth style={{ marginBottom: 12 }}>
                <InputLabel shrink>Operator</InputLabel>
                <Select
                  value={addOperatorId}
                  onChange={e => { if (e.target.value !== '__search__') setAddOperatorId(e.target.value); }}
                  label="Operator"
                  displayEmpty
                  renderValue={() => {
                    const op = (operators || []).find(o => o.operatorId === addOperatorId);
                    return op ? `${op.operatorId} – ${op.name}` : <span style={{ color: '#aaa' }}>Search operator…</span>;
                  }}
                  MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 300 } } }}
                  onOpen={() => setAddOperatorSearch('')}
                >
                  <MenuItem value="__search__" disableRipple style={{ padding: '4px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}
                    onKeyDown={e => e.stopPropagation()}>
                    <TextField
                      autoFocus size="small" variant="outlined" fullWidth placeholder="Search by ID or name…"
                      value={addOperatorSearch}
                      onChange={e => setAddOperatorSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                  </MenuItem>
                  {(operators || [])
                    .filter(op =>
                      !addOperatorSearch ||
                      op.operatorId.toLowerCase().includes(addOperatorSearch.toLowerCase()) ||
                      op.name.toLowerCase().includes(addOperatorSearch.toLowerCase())
                    )
                    .map(op => (
                      <MenuItem key={op.operatorId} value={op.operatorId}>{op.operatorId} – {op.name}</MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* ── Searchable Master Line Item picker ── */}
              <FormControl size="small" variant="outlined" fullWidth style={{ marginBottom: 12 }}>
                <InputLabel shrink>Master Line Item</InputLabel>
                <Select
                  value={addMasterRef ? addMasterRef._id : ''}
                  onChange={e => { if (e.target.value !== '__search__') handleAddMasterRefChange(allMasterItems.find(m => m._id === e.target.value) || null); }}
                  label="Master Line Item"
                  displayEmpty
                  renderValue={() => {
                    return addMasterRef ? addMasterRef.billingItemName : <span style={{ color: '#aaa' }}>Search billing item…</span>;
                  }}
                  MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 300 } } }}
                  onOpen={() => setAddMasterSearch('')}
                >
                  <MenuItem value="__search__" disableRipple style={{ padding: '4px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}
                    onKeyDown={e => e.stopPropagation()}>
                    <TextField
                      autoFocus size="small" variant="outlined" fullWidth placeholder="Search billing item name…"
                      value={addMasterSearch}
                      onChange={e => setAddMasterSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                  </MenuItem>
                  {allMasterItems
                    .filter(m =>
                      !addMasterSearch ||
                      m.billingItemName.toLowerCase().includes(addMasterSearch.toLowerCase()) ||
                      (m.displayItemName || '').toLowerCase().includes(addMasterSearch.toLowerCase())
                    )
                    .map(m => (
                      <MenuItem key={m._id} value={m._id}>{m.billingItemName}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              {addMasterRef && (
                <Box className={classes.masterHint} mt={1}>
                  <Typography variant="caption" color="textSecondary">Master values (reference only — only prices are overridable)</Typography>
                  <Typography variant="body2">Purchase: <strong>${addMasterRef.purchasePrice}</strong> &nbsp; Sale: <strong>${addMasterRef.salePrice}</strong> &nbsp; GST: <strong>{addMasterRef.GSTPercentage}%</strong></Typography>
                </Box>
              )}
            </Box>
          )}

          <div className={classes.fieldGrid}>
            {addTarget === 'master' && MASTER_ONLY_FIELDS.filter(f => f !== 'GSTApplicable' && f !== 'GSTPercentage').map(f => (
              <TextField key={f} label={f} size="small" variant="outlined" value={addForm[f] ?? ''}
                onChange={e => {
                  const value = e.target.value;
                  setAddForm(p => ({ ...p, [f]: value }));
                }} />
            ))}
            {addTarget === 'master' && (
              <>
                <TextField label="GST %" size="small" variant="outlined" type="number"
                  value={addForm.GSTPercentage} onChange={e => {
                    const value = +e.target.value;
                    setAddForm(p => ({ ...p, GSTPercentage: value }));
                  }} />
                <FormControlLabel control={<Switch checked={addForm.GSTApplicable} color="primary"
                  onChange={e => {
                    const checked = e.target.checked;
                    setAddForm(p => ({ ...p, GSTApplicable: checked }));
                  }} />} label="GST Applicable" />
              </>
            )}
            {addTarget === 'operator' && addMasterRef && (
              <>
                <FormControlLabel
                  style={{ gridColumn: '1 / -1' }}
                  control={<Switch checked={addForm.useMaster !== false} color="primary"
                    onChange={e => {
                      const checked = e.target.checked;
                      // When useMaster is enabled, set prices to 0 (inherit from master)
                      setAddForm(p => ({
                        ...p,
                        useMaster: checked,
                        purchasePrice: checked ? 0 : p.purchasePrice,
                        salePrice: checked ? 0 : p.salePrice
                      }));
                    }} />}
                  label={<Box><Typography variant="body2"><strong>Use Master Values</strong></Typography><Typography variant="caption" color="textSecondary">Inherit all prices and GST settings from master item</Typography></Box>}
                />
                <FormControlLabel
                  style={{ gridColumn: '1 / -1' }}
                  control={<Switch checked={!!addForm.excludeFromBilling} color="secondary"
                    onChange={e => {
                      const checked = e.target.checked;
                      setAddForm(p => ({ ...p, excludeFromBilling: checked }));
                    }} />}
                  label={<Box><Typography variant="body2">Exclude from billing for this operator</Typography><Typography variant="caption" color="textSecondary">e.g. a discount Thinxtra is absorbing for this operator</Typography></Box>}
                />
              </>
            )}
            {/* Prices editable in both targets */}
            <TextField label="Purchase Price $" size="small" variant="outlined" type="number"
              value={addForm.purchasePrice}
              disabled={addTarget === 'operator' && addForm.useMaster !== false}
              onChange={e => {
                const value = +e.target.value;
                setAddForm(p => ({ ...p, purchasePrice: value }));
              }} />
            <TextField label="Sale Price $" size="small" variant="outlined" type="number"
              value={addForm.salePrice}
              disabled={addTarget === 'operator' && addForm.useMaster !== false}
              onChange={e => {
                const value = +e.target.value;
                setAddForm(p => ({ ...p, salePrice: value }));
              }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleAddSave}>
            Add to {addTarget === 'master' ? 'Master Sheet' : `${addOperatorId || 'Operator'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════ DETAIL DRAWER (master / operator edit) ══════════════════ */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDetail}>
        <div className={classes.drawer}>
          {selected && (
            <>
              <div className={classes.drawerHeader}>
                <Typography variant="h6">{selected.isMaster ? '📋 Master Item' : '🔧 Operator Override'}</Typography>
                <IconButton size="small" onClick={closeDetail}><CloseIcon /></IconButton>
              </div>
              <Divider />
              <div className={classes.drawerBody}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <FormControlLabel
                    control={<Switch checked={editMode} onChange={e => setEditMode(e.target.checked)} color="primary" size="small" />}
                    label="Edit mode" />
                </Box>

                {/* Master-only fields — editable only when isMaster */}
                <Typography variant="caption" color="textSecondary">Master fields</Typography>
                <div className={classes.fieldGrid} style={{ marginTop: 4 }}>
                  {MASTER_ONLY_FIELDS.filter(f => f !== 'GSTApplicable' && f !== 'GSTPercentage').map(f => (
                    <TextField key={f} label={f} size="small" variant="outlined"
                      value={editValues[f] ?? ''}
                      disabled={!editMode || !selected.isMaster}
                      className={!selected.isMaster ? classes.readonlyField : ''}
                      onChange={e => handleFieldChange(f, e.target.value)} />
                  ))}
                  <TextField label="GST %" size="small" variant="outlined" type="number"
                    value={editValues.GSTPercentage ?? ''}
                    disabled={!editMode || (!selected.isMaster && editValues.useMaster)}
                    onChange={e => handleFieldChange('GSTPercentage', +e.target.value)} />
                  <FormControlLabel control={<Switch checked={!!editValues.GSTApplicable}
                    disabled={!editMode || (!selected.isMaster && editValues.useMaster)} color="primary" size="small"
                    onChange={e => handleFieldChange('GSTApplicable', e.target.checked)} />} label="GST Applicable" />
                </div>

                {!selected.isMaster && (
                  <>
                    <Box mb={2} p={1.5} style={{ background: '#e3f2fd', borderRadius: 6, border: '1px solid #90caf9' }}>
                      <FormControlLabel
                        control={<Switch checked={!!editValues.useMaster} color="primary" size="small"
                          disabled={!editMode}
                          onChange={e => handleFieldChange('useMaster', e.target.checked)} />}
                        label={<Box><Typography variant="body2"><strong>Use Master Values</strong></Typography><Typography variant="caption" color="textSecondary">When enabled, all prices and GST settings are inherited from master item. Changes to master automatically reflect here.</Typography></Box>}
                      />
                    </Box>
                    <Box mb={2} p={1.5} style={{ background: editValues.excludeFromBilling ? '#fff3e0' : '#f5f5f5', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                      <FormControlLabel
                        control={<Switch checked={!!editValues.excludeFromBilling} color="secondary" size="small"
                          disabled={!editMode}
                          onChange={e => handleFieldChange('excludeFromBilling', e.target.checked)} />}
                        label={<Box><Typography variant="body2">Exclude from billing for this operator</Typography><Typography variant="caption" color="textSecondary">Item is visible in the UI but will NOT appear on this operator's invoice</Typography></Box>}
                      />
                    </Box>
                  </>
                )}

                <Divider style={{ margin: '12px 0' }} />
                <Typography variant="caption" color="textSecondary">
                  {!selected.isMaster && editValues.useMaster ? 'Prices (inherited from master)' : `Prices ${!selected.isMaster ? '(operator override)' : ''}`}
                </Typography>
                <div className={classes.fieldGrid} style={{ marginTop: 4 }}>
                  {OPERATOR_OVERRIDABLE_FIELDS.map(f => (
                    <TextField key={f} label={f === 'purchasePrice' ? 'Purchase Price $' : 'Sale Price $'}
                      size="small" variant="outlined" type="number"
                      value={editValues[f] ?? ''}
                      disabled={!editMode || (!selected.isMaster && editValues.useMaster)}
                      onChange={e => handleFieldChange(f, +e.target.value)} />
                  ))}
                </div>

                {/* Show master reference prices when editing operator item */}
                {!selected.isMaster && masterRef && (
                  <Box className={classes.masterHint}>
                    <Typography variant="caption" color="textSecondary">Master reference prices</Typography>
                    <Typography variant="body2">Purchase: <strong>${masterRef.purchasePrice}</strong> &nbsp; Sale: <strong>${masterRef.salePrice}</strong></Typography>
                  </Box>
                )}

                {editMode && (
                  <Box display="flex" style={{ gap: 8 }} mt={1} mb={2}>
                    <Button variant="contained" color="primary" size="small" onClick={handleSave}>Save</Button>
                    <Button variant="outlined" size="small" onClick={() => { setEditValues({ ...selected }); setEditMode(false); }}>Cancel</Button>
                  </Box>
                )}

                {/* Grouped line items */}
                {groupedItems && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography variant="subtitle2" className={classes.sectionTitle}>Grouped as — <em>{groupedItems.displayItemName}</em></Typography>
                    {groupedItems.items.map(item => <Chip key={item} size="small" label={item} className={classes.chip} style={{ marginBottom: 4 }} />)}
                  </>
                )}

                {/* Operator overrides table (master view only) */}
                {selected.isMaster && operatorOverrides.length > 0 && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography variant="subtitle2" className={classes.sectionTitle}>Operator Overrides ({operatorOverrides.length})</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow><TableCell>Operator</TableCell><TableCell>Purchase $</TableCell><TableCell>Sale $</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                          {operatorOverrides.map(o => (
                            <TableRow key={o._id}>
                              <TableCell>{o.operatorId}</TableCell>
                              <TableCell style={{ color: o.purchasePrice !== selected.purchasePrice ? '#e65100' : 'inherit' }}>${o.purchasePrice.toFixed(BILLING_DECIMAL_PLACES)}</TableCell>
                              <TableCell style={{ color: o.salePrice !== selected.salePrice ? '#e65100' : 'inherit' }}>${o.salePrice.toFixed(BILLING_DECIMAL_PLACES)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Drawer>

      {/* ══════════════════ REVIEW ITEM DRAWER ══════════════════ */}
      <Drawer anchor="right" open={reviewDrawerOpen} onClose={() => setReviewDrawerOpen(false)}>
        <div className={classes.drawer}>
          {reviewSelected && (
            <>
              <div className={classes.drawerHeader}>
                <Typography variant="h6">
                  {reviewSelected.reviewType === 'MISSING_FROM_MASTER' ? '⚠️ New — Not in Master' : '📝 Set Operator Overrides'}
                </Typography>
                <IconButton size="small" onClick={() => setReviewDrawerOpen(false)}><CloseIcon /></IconButton>
              </div>
              <Divider />
              <div className={classes.drawerBody}>
                <Typography variant="caption" color="textSecondary">
                  Operator: <strong>{reviewSelected.operatorId} – {reviewSelected.operatorName}</strong>
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 2 }}>
                  Item: <strong>{reviewSelected.rawBillingItemName}</strong>
                </Typography>

                {/* ── MISSING_FROM_MASTER: fill in all master fields ── */}
                {reviewSelected.reviewType === 'MISSING_FROM_MASTER' && (
                  <>
                    <Box mt={1} mb={1} style={{ background: '#fce4ec', padding: 8, borderRadius: 6 }}>
                      <Typography variant="body2">This billing item does not exist in the master sheet. Fill in all master details to create it, then set operator override settings.</Typography>
                    </Box>
                    <Typography variant="subtitle2" className={classes.sectionTitle}>Master Sheet Details</Typography>
                    <div className={classes.fieldGrid}>
                      <TextField label="Billing Item Name" size="small" variant="outlined"
                        value={reviewEditValues.billingItemName ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, billingItemName: v })); }} />
                      <TextField label="Display Name" size="small" variant="outlined"
                        value={reviewEditValues.displayItemName ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, displayItemName: v })); }} />
                      <TextField label="Charge Code" size="small" variant="outlined"
                        value={reviewEditValues.chargeCode ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, chargeCode: v })); }} />
                      <TextField label="Unit" size="small" variant="outlined"
                        value={reviewEditValues.unit ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, unit: v })); }} />
                      <TextField label="Soracom Invoice Item Name" size="small" variant="outlined"
                        style={{ gridColumn: '1 / -1' }}
                        value={reviewEditValues.soracomInvoiceItemName ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, soracomInvoiceItemName: v })); }} />
                      <TextField label="Master Purchase Price $" size="small" variant="outlined" type="number"
                        value={reviewEditValues.masterPurchasePrice ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, masterPurchasePrice: v })); }} />
                      <TextField label="Master Sale Price $" size="small" variant="outlined" type="number"
                        value={reviewEditValues.masterSalePrice ?? ''}
                        onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, masterSalePrice: v })); }} />
                      <TextField label="GST %" size="small" variant="outlined" type="number"
                        value={reviewEditValues.GSTPercentage ?? 10}
                        onChange={e => { const v = +e.target.value; setReviewEditValues(p => ({ ...p, GSTPercentage: v })); }} />
                      <FormControlLabel
                        control={<Switch checked={!!reviewEditValues.GSTApplicable} color="primary" size="small"
                          onChange={e => { const v = e.target.checked; setReviewEditValues(p => ({ ...p, GSTApplicable: v })); }} />}
                        label="GST Applicable" />
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                  </>
                )}

                {/* ── MISSING_FOR_OPERATOR: show master details as read-only ── */}
                {reviewSelected.reviewType === 'MISSING_FOR_OPERATOR' && (
                  <Box className={classes.masterHint} mt={1} mb={1}>
                    <Typography variant="caption" color="textSecondary">Master item reference (read-only)</Typography>
                    <div className={classes.fieldGrid} style={{ marginTop: 8, marginBottom: 0 }}>
                      <TextField label="Billing Item Name" size="small" variant="outlined"
                        value={reviewEditValues.billingItemName ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                      <TextField label="Display Name" size="small" variant="outlined"
                        value={reviewEditValues.displayItemName ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                      <TextField label="Charge Code" size="small" variant="outlined"
                        value={reviewEditValues.chargeCode ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                      <TextField label="Unit" size="small" variant="outlined"
                        value={reviewEditValues.unit ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                      <TextField label="Master Purchase $" size="small" variant="outlined"
                        value={reviewEditValues.masterPurchasePrice ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                      <TextField label="Master Sale $" size="small" variant="outlined"
                        value={reviewEditValues.masterSalePrice ?? ''} InputProps={{ readOnly: true }} className={classes.readonlyField} />
                    </div>
                  </Box>
                )}

                {/* ── Operator Override Settings (both types) ── */}
                <Typography variant="subtitle2" className={classes.sectionTitle}>Operator Override Settings</Typography>
                <div className={classes.fieldGrid}>
                  <FormControlLabel
                    control={<Switch checked={!!reviewEditValues.useMaster} color="primary" size="small"
                      onChange={e => { const v = e.target.checked; setReviewEditValues(p => ({ ...p, useMaster: v })); }} />}
                    label="Use Master Pricing" />
                  <FormControlLabel
                    control={<Switch checked={!!reviewEditValues.excludeFromBilling} color="secondary" size="small"
                      onChange={e => { const v = e.target.checked; setReviewEditValues(p => ({ ...p, excludeFromBilling: v })); }} />}
                    label="Exclude From Billing" />
                </div>
                {!reviewEditValues.useMaster && (
                  <div className={classes.fieldGrid}>
                    <TextField label="Operator Purchase Price $" size="small" variant="outlined" type="number"
                      value={reviewEditValues.operatorPurchasePrice ?? ''}
                      onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, operatorPurchasePrice: v })); }} />
                    <TextField label="Operator Sale Price $" size="small" variant="outlined" type="number"
                      value={reviewEditValues.operatorSalePrice ?? ''}
                      onChange={e => { const v = e.target.value; setReviewEditValues(p => ({ ...p, operatorSalePrice: v })); }} />
                  </div>
                )}

                <Box display="flex" style={{ gap: 8 }} mt={2}>
                  <Button variant="contained" color="primary" size="small"
                    disabled={
                      reviewSelected.reviewType === 'MISSING_FROM_MASTER' && (
                        !reviewEditValues.billingItemName ||
                        !reviewEditValues.displayItemName ||
                        !reviewEditValues.chargeCode ||
                        !reviewEditValues.unit ||
                        !reviewEditValues.soracomInvoiceItemName
                      )
                    }
                    onClick={() => {
                      const masterData = reviewSelected.reviewType === 'MISSING_FROM_MASTER' ? {
                        billingItemName: reviewEditValues.billingItemName,
                        displayItemName: reviewEditValues.displayItemName,
                        chargeCode: reviewEditValues.chargeCode,
                        unit: reviewEditValues.unit,
                        soracomInvoiceItemName: reviewEditValues.soracomInvoiceItemName,
                        purchasePrice: parseFloat(reviewEditValues.masterPurchasePrice) || 0,
                        salePrice: parseFloat(reviewEditValues.masterSalePrice) || 0,
                        GSTApplicable: !!reviewEditValues.GSTApplicable,
                        GSTPercentage: parseFloat(reviewEditValues.GSTPercentage) || 10,
                      } : {};
                      const operatorData = {
                        purchasePrice: reviewEditValues.useMaster ? 0 : parseFloat(reviewEditValues.operatorPurchasePrice) || 0,
                        salePrice: reviewEditValues.useMaster ? 0 : parseFloat(reviewEditValues.operatorSalePrice) || 0,
                        useMaster: !!reviewEditValues.useMaster,
                        excludeFromBilling: !!reviewEditValues.excludeFromBilling,
                      };
                      handleResolve(reviewSelected, masterData, operatorData);
                    }}>
                    {reviewSelected.reviewType === 'MISSING_FROM_MASTER' ? 'Create in Master & Approve' : 'Approve & Close'}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setReviewDrawerOpen(false)}>Cancel</Button>
                </Box>
              </div>
            </>
          )}
        </div>
      </Drawer>

      {/* ══════════════════ DIFF DIALOG ══════════════════ */}
      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Preview</DialogTitle>
        <DialogContent dividers>
          {diffResults.filter(d => d._diffStatus !== 'UNCHANGED').length === 0 && (
            <Typography color="textSecondary" variant="body2" style={{ marginTop: 16 }}>No changes detected.</Typography>
          )}
          {['NEW', 'MODIFIED'].map(status => {
            const items = diffResults.filter(d => d._diffStatus === status);
            if (!items.length) return null;
            return (
              <Box key={status} mb={2}>
                <Typography variant="subtitle2" gutterBottom style={{ color: status === 'NEW' ? '#1565c0' : '#e65100' }}>
                  {status === 'NEW' ? `✚ New items (${items.length})` : `✎ Modified items (${items.length})`}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Billing Item Name</TableCell>
                        {status === 'NEW' ? <><TableCell>Charge Code</TableCell><TableCell align="right">Purchase $</TableCell><TableCell align="right">Sale $</TableCell></> : <><TableCell>Field</TableCell><TableCell>From</TableCell><TableCell>To</TableCell></>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {status === 'NEW' && items.map((d, i) => (
                        <TableRow key={i} style={{ background: '#e3f2fd' }}>
                          <TableCell>{d.billingItemName}</TableCell>
                          <TableCell>{d.chargeCode}</TableCell>
                          <TableCell align="right">{d.purchasePrice}</TableCell>
                          <TableCell align="right">{d.salePrice}</TableCell>
                        </TableRow>
                      ))}
                      {status === 'MODIFIED' && items.flatMap((d, i) =>
                        (d._changes || []).map((c, j) => (
                          <TableRow key={`${i}-${j}`} style={{ background: j % 2 === 0 ? '#fff8e1' : '#fffde7' }}>
                            {j === 0 && <TableCell rowSpan={(d._changes || []).length}><strong>{d.billingItemName}</strong></TableCell>}
                            <TableCell>{c.field}</TableCell>
                            <TableCell style={{ color: '#c62828' }}>{String(c.from)}</TableCell>
                            <TableCell style={{ color: '#2e7d32' }}>{String(c.to)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button disabled={applyingChanges} onClick={() => setDiffDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary"
            disabled={applyingChanges || diffResults.filter(d => d._diffStatus !== 'UNCHANGED').length === 0}
            onClick={handleApplyChanges}
            startIcon={applyingChanges ? <CircularProgress size={16} color="inherit" /> : null}>
            {applyingChanges ? 'Applying…' : `Apply ${diffResults.filter(d => d._diffStatus !== 'UNCHANGED').length} Changes`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════ CSV IMPORT RESULTS DIALOG (REVIEW ITEMS) ══════════════════ */}
      <Dialog
        open={importResultsDialogOpen}
        onClose={() => setImportResultsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          CSV Import Results
        </DialogTitle>
        <DialogContent dividers>
          {importResults && (
            <>
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={`Total Rows: ${importResults.summary.totalRows}`}
                    color="default"
                  />
                  <Chip
                    label={`Masters Created: ${importResults.summary.mastersCreated}`}
                    style={{ backgroundColor: '#4caf50', color: 'white' }}
                  />
                  <Chip
                    label={`Operator Overrides Created: ${importResults.summary.operatorOverridesCreated}`}
                    style={{ backgroundColor: '#2196f3', color: 'white' }}
                  />
                  <Chip
                    label={`Review Items Resolved: ${importResults.summary.reviewItemsRemoved}`}
                    style={{ backgroundColor: '#ff9800', color: 'white' }}
                  />
                  {importResults.summary.failed > 0 && (
                    <Chip
                      label={`Failed: ${importResults.summary.failed}`}
                      style={{ backgroundColor: '#f44336', color: 'white' }}
                    />
                  )}
                </Box>
              </Box>

              {importResults.success.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle1" gutterBottom style={{ color: '#4caf50' }}>
                    ✅ Successfully Imported ({importResults.success.length})
                  </Typography>
                  <Box maxHeight={200} overflow="auto">
                    {importResults.success.map((item, idx) => (
                      <Box key={idx} mb={1} p={1} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <Typography variant="body2">
                          <strong>Row {item.row}:</strong> {item.operatorId} - {item.billingItemName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {item.masterCreated && '• Master item created '}
                          {item.operatorCreated && '• Operator override created '}
                          {item.reviewItemRemoved && '• Removed from review queue'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {importResults.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom style={{ color: '#f44336' }}>
                    ❌ Errors ({importResults.errors.length})
                  </Typography>
                  <Box maxHeight={200} overflow="auto">
                    {importResults.errors.map((err, idx) => (
                      <Box key={idx} mb={1} p={1} style={{ backgroundColor: '#ffebee', borderRadius: 4 }}>
                        <Typography variant="body2" color="error">
                          <strong>Row {err.row}:</strong> {err.operatorId} - {err.billingItemName}
                        </Typography>
                        <Typography variant="caption" color="error">
                          {err.error}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultsDialogOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════ HISTORY DRAWER ══════════════════ */}
      <Drawer anchor="right" open={historyDrawerOpen} onClose={() => setHistoryDrawerOpen(false)}>
        <div className={classes.drawer}>
          <div className={classes.drawerHeader}>
            <div>
              <Typography variant="h6">Change History</Typography>
              {historyItem && (
                <Typography variant="caption" color="textSecondary">{historyItem.billingItemName}{historyItem.operatorId ? ` · ${historyItem.operatorId}` : ''}</Typography>
              )}
            </div>
            <IconButton size="small" onClick={() => setHistoryDrawerOpen(false)}><CloseIcon /></IconButton>
          </div>
          <Divider />
          <div className={classes.drawerBody}>
            {(() => {
              const resourceId = historyItem?._id?.toString();
              const itemLogs = allActivityLogs.filter(a =>
                a.resource?.id === resourceId || a.resource?.displayName === historyItem?.billingItemName
              );
              if (!itemLogs.length) return (
                <Typography color="textSecondary" variant="body2" style={{ marginTop: 16 }}>No history found for this item.</Typography>
              );
              return itemLogs.map((a, i) => (
                <Box key={i} mb={2} p={1.5} style={{ background: '#f9f9f9', borderRadius: 6, borderLeft: '3px solid #1976d2' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip size="small" label={a.action} color={ACTION_COLORS[a.action] || 'default'} />
                    <Typography variant="caption" color="textSecondary"><code>{new Date(a.timestamp).toLocaleString()}</code></Typography>
                  </Box>
                  {a.changes?.length > 0 && a.changes.map((c, j) => (
                    <Typography key={j} variant="caption" display="block" style={{ marginTop: 4 }}>
                      <strong>{c.field}</strong>: {String(c.oldValue)} → {String(c.newValue)}
                    </Typography>
                  ))}
                  {a.metadata && !a.changes?.length && (
                    <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                      {typeof a.metadata === 'object' ? JSON.stringify(a.metadata) : a.metadata}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">{a.actor?.email || '—'}</Typography>
                </Box>
              ));
            })()}
          </div>
        </div>
      </Drawer>

    </>
  );
};

export default withBase(connect(null, billingActions)(MasterSheet));
